import React, { useState } from 'react';
import { 
  Edit3, 
  RotateCcw,
  Type,
  Settings
} from 'lucide-react';

const ContentManager = () => {
  const [heroTexts, setHeroTexts] = useState([
    {
      id: 1,
      language: 'tr',
      title: 'Karbon Nötr Geleceğe Adım Atın',
      subtitle: 'CO₂ emisyonlarınızı azaltın, dijital varlıklarınızı nötr hale getirin',
      active: true
    },
    {
      id: 2,
      language: 'tr',
      title: 'Dijital Dünyada Sıfır Karbon',
      subtitle: 'Web sitenizi çevre dostu hale getirin, geleceğe yatırım yapın',
      active: true
    },
    {
      id: 3,
      language: 'de',
      title: 'Schritt in eine CO₂-neutrale Zukunft',
      subtitle: 'Reduzieren Sie Ihre CO₂-Emissionen, machen Sie Ihre digitalen Assets neutral',
      active: true
    },
    {
      id: 4,
      language: 'en',
      title: 'Step into a CO₂ Neutral Future',
      subtitle: 'Reduce your CO₂ emissions, make your digital assets neutral',
      active: true
    }
  ]);

  const [editingText, setEditingText] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('tr');

  const handleEditText = (text) => {
    setEditingText({ ...text });
  };

  const handleSaveText = () => {
    setHeroTexts(heroTexts.map(text => 
      text.id === editingText.id ? editingText : text
    ));
    setEditingText(null);
  };

  const handleToggleActive = (id) => {
    setHeroTexts(heroTexts.map(text => 
      text.id === id ? { ...text, active: !text.active } : text
    ));
  };

  const handleAddText = () => {
    const newText = {
      id: Math.max(...heroTexts.map(t => t.id)) + 1,
      language: selectedLanguage,
      title: '',
      subtitle: '',
      active: true
    };
    setHeroTexts([...heroTexts, newText]);
    setEditingText(newText);
  };

  const handleDeleteText = (id) => {
    if (window.confirm('Bu metni silmek istediğinizden emin misiniz?')) {
      setHeroTexts(heroTexts.filter(text => text.id !== id));
    }
  };

  const filteredTexts = heroTexts.filter(text => text.language === selectedLanguage);

  const getLanguageLabel = (language) => {
    switch (language) {
      case 'tr': return 'Türkçe';
      case 'de': return 'Deutsch';
      case 'en': return 'English';
      default: return language;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-neutral-800">İçerik Yönetimi</h2>
        <div className="flex space-x-3">
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="tr">Türkçe</option>
            <option value="de">Deutsch</option>
            <option value="en">English</option>
          </select>
          <button
            onClick={handleAddText}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Type className="h-4 w-4" />
            <span>Yeni Metin</span>
          </button>
        </div>
      </div>

      {/* Hero Texts Management */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200">
        <div className="p-6 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
            <Type className="h-5 w-5" />
            <span>Hero Metinleri - {getLanguageLabel(selectedLanguage)}</span>
          </h3>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            {filteredTexts.map((text) => (
              <div key={text.id} className="border border-neutral-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className={`w-2 h-2 rounded-full ${text.active ? 'bg-green-500' : 'bg-neutral-300'}`}></span>
                    <span className="text-sm font-medium text-neutral-600">Metin #{text.id}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditText(text)}
                      className="text-primary-600 hover:text-primary-900 p-1"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(text.id)}
                      className={`p-1 rounded ${
                        text.active 
                          ? 'text-green-600 hover:text-green-900' 
                          : 'text-neutral-400 hover:text-neutral-600'
                      }`}
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteText(text.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Başlık
                    </label>
                    <p className="text-sm text-neutral-800 bg-neutral-50 p-2 rounded">
                      {text.title}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Alt Başlık
                    </label>
                    <p className="text-sm text-neutral-600 bg-neutral-50 p-2 rounded">
                      {text.subtitle}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Site Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200">
        <div className="p-6 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Site Ayarları</span>
          </h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Site Başlığı
              </label>
              <input
                type="text"
                defaultValue="CO2 Neutral - Karbon Nötr Geleceğe Adım Atın"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Site Açıklaması
              </label>
              <input
                type="text"
                defaultValue="CO₂ emisyonlarınızı azaltın, dijital varlıklarınızı nötr hale getirin"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                İletişim E-posta
              </label>
              <input
                type="email"
                defaultValue="info@co2neutralpage.com"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Telefon
              </label>
              <input
                type="text"
                name="phone"
                placeholder="Phone number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                defaultValue=""
              />
            </div>
          </div>
          
          <div className="mt-6">
            <button className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2 rounded-lg transition-colors">
              Ayarları Kaydet
            </button>
          </div>
        </div>
      </div>

      {/* Edit Text Modal */}
      {editingText && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">
              Metin Düzenle - {getLanguageLabel(editingText.language)}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Başlık
                </label>
                <input
                  type="text"
                  value={editingText.title}
                  onChange={(e) => setEditingText({...editingText, title: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Alt Başlık
                </label>
                <textarea
                  value={editingText.subtitle}
                  onChange={(e) => setEditingText({...editingText, subtitle: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  checked={editingText.active}
                  onChange={(e) => setEditingText({...editingText, active: e.target.checked})}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-neutral-700">
                  Aktif
                </label>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleSaveText}
                className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Kaydet
              </button>
              <button
                onClick={() => setEditingText(null)}
                className="flex-1 bg-neutral-200 hover:bg-neutral-300 text-neutral-800 py-2 px-4 rounded-lg transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManager; 